import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  ShoppingCart,
  Search,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  Truck,
  FileText,
  CreditCard,
  Banknote,
  Download,
  FileSpreadsheet,
  RefreshCw,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const DELIVERY_FILTER_OPTIONS = [
    { id: 'local', label: 'Personal Local Delivery ($5)' },
    { id: 'rural', label: 'Personal Delivery ($8)' },
    { id: 'mail', label: 'Direct from Lab ($10)' },
    { id: 'none', label: 'No Delivery / None Chosen' },
  ];
  const [deliveryFilters, setDeliveryFilters] = useState(DELIVERY_FILTER_OPTIONS.map(o => o.id));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState({ open: false, order: null });
  const [paymentMethod, setPaymentMethod] = useState('etransfer');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [selectedForPrint, setSelectedForPrint] = useState([]);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [includePreorders, setIncludePreorders] = useState(true);
  const [backgrounds, setBackgrounds] = useState({});

  useEffect(() => {
    fetchOrders();
    fetchBackgrounds();
  }, [statusFilter, paymentFilter, startDate, endDate, includePreorders]);

  const fetchBackgrounds = async () => {
    try {
      const response = await axios.get(`${API}/backgrounds`);
      // Create a lookup map by ID
      const bgMap = {};
      response.data.forEach(bg => {
        bgMap[bg.id] = bg.name;
      });
      setBackgrounds(bgMap);
    } catch (error) {
      console.error('Failed to load backgrounds');
    }
  };

  const fetchOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (paymentFilter !== 'all') params.append('payment_status', paymentFilter);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (includePreorders) params.append('include_preorders', 'true');
      
      const response = await axios.get(`${API}/orders?${params}`);
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      // Check if this is a pre-order or regular order
      const isPreorder = selectedOrder?.order_type === 'pre-order';
      
      const endpoint = isPreorder 
        ? `${API}/preorders/${orderId}/status?status=${newStatus}`
        : `${API}/orders/${orderId}/status?status=${newStatus}`;
      
      await axios.put(endpoint);
      toast.success('Order status updated');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus, original_status: newStatus });
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Failed to update status');
    }
  };

  const reopenPreorderFromOrders = async (order) => {
    if (!window.confirm(`Re-open ${order.customer_name || order.child_name || 'this client'}'s order? They'll get an email letting them know they can edit their order via their review link.`)) return;
    try {
      const res = await axios.post(`${API}/preorders/${order.id}/reopen`, { base_url: window.location.origin });
      if (res.data.email_sent) {
        toast.success('Order re-opened and the client has been emailed their edit link');
      } else {
        toast.warning('Order re-opened, but the notification email could not be sent');
      }
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-open order');
    }
  };

  const markOfflinePayment = async () => {
    if (!paymentDialog.order) return;
    try {
      const order = paymentDialog.order;
      const isPreorder = order.order_type === 'pre-order';
      
      // Calculate balance
      const total = order.total || 0;
      const amountPaid = order.amount_paid || 0;
      const balance = total - amountPaid;
      
      // Parse payment amount (use balance if empty)
      const amount = paymentAmount ? parseFloat(paymentAmount) : balance;
      
      if (isPreorder) {
        // Use the pre-order mark-paid endpoint
        await axios.put(`${API}/preorders/${order.id}/mark-paid`, {
          payment_method: paymentMethod,
          notes: paymentNotes,
          amount: amount
        });
      } else {
        // Use the regular order offline-payment endpoint
        await axios.post(`${API}/orders/${order.id}/offline-payment`, {
          order_id: order.id,
          payment_method: paymentMethod,
          notes: paymentNotes,
          amount: amount
        });
      }
      
      const isPartial = amount < balance;
      toast.success(isPartial 
        ? `Partial payment of $${amount.toFixed(2)} recorded. Balance: $${(balance - amount).toFixed(2)}`
        : `Order marked as fully paid via ${paymentMethod}`
      );
      setPaymentDialog({ open: false, order: null });
      setPaymentNotes('');
      setPaymentAmount('');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark payment');
    }
  };

  const downloadPrintSheet = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/print-sheet`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `order_${orderId.slice(0, 8)}_print_sheet.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Print sheet downloaded');
    } catch (error) {
      toast.error('Failed to generate print sheet');
    }
  };

  const downloadBatchPrintSheets = async () => {
    if (selectedForPrint.length === 0) {
      toast.error('Please select orders to print');
      return;
    }
    try {
      const response = await axios.get(`${API}/orders/batch/print-sheets?order_ids=${selectedForPrint.join(',')}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'batch_print_sheets.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Batch print sheets downloaded');
      setSelectedForPrint([]);
    } catch (error) {
      toast.error('Failed to generate batch print sheets');
    }
  };

  const togglePrintSelect = (orderId) => {
    setSelectedForPrint(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'approved': return <CheckCircle className="h-4 w-4 text-blue-600" />;
      case 'processing': return <Package className="h-4 w-4 text-amber-600" />;
      case 'ready_for_review': return <Package className="h-4 w-4 text-amber-600" />;
      case 'shipped': return <Truck className="h-4 w-4 text-purple-600" />;
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'fulfilled': return <CheckCircle className="h-4 w-4 text-emerald-700" />;
      case 'cancelled': return <XCircle className="h-4 w-4 text-red-600" />;
      default: return <Clock className="h-4 w-4 text-amber-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'processing': return 'bg-amber-100 text-amber-700';
      case 'ready_for_review': return 'bg-amber-100 text-amber-700';
      case 'shipped': return 'bg-purple-100 text-purple-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'fulfilled': return 'bg-emerald-100 text-emerald-800';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  const getPaymentColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'partial': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPaymentLabel = (order) => {
    if (order.payment_status === 'partial') {
      const paid = order.amount_paid || 0;
      const total = order.total || 0;
      return `Partial ($${paid.toFixed(2)}/${total.toFixed(2)})`;
    }
    return order.payment_status?.charAt(0).toUpperCase() + order.payment_status?.slice(1);
  };

  const exportToExcel = async () => {
    setExportingExcel(true);
    try {
      // Export exactly what's selected, or everything currently shown (all filters applied)
      const exportIds = selectedForPrint.length > 0 ? selectedForPrint : filteredOrders.map(o => o.id);
      if (exportIds.length === 0) {
        toast.error('No orders to export');
        setExportingExcel(false);
        return;
      }
      const response = await axios.post(`${API}/orders/export/excel`, {
        order_ids: exportIds,
        include_preorders: includePreorders
      }, { responseType: 'blob' });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders_${exportIds.length}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(selectedForPrint.length > 0
        ? `Exported ${exportIds.length} selected order(s) to Excel`
        : `Exported ${exportIds.length} shown order(s) to Excel`
      );
    } catch (error) {
      toast.error('Failed to export orders');
    } finally {
      setExportingExcel(false);
    }
  };

  const exportDeliveryCsv = async () => {
    setExportingExcel(true);
    try {
      const exportIds = selectedForPrint.length > 0 ? selectedForPrint : filteredOrders.map(o => o.id);
      if (exportIds.length === 0) {
        toast.error('No orders to export');
        setExportingExcel(false);
        return;
      }
      const response = await axios.post(`${API}/orders/export/delivery-csv`, {
        order_ids: exportIds,
        include_preorders: includePreorders
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `delivery_list_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Delivery list CSV downloaded');
    } catch (error) {
      toast.error('Failed to export delivery list');
    } finally {
      setExportingExcel(false);
    }
  };

  // Delivery method: from the delivery sub-doc, or (recovered orders) from a delivery line item
  const getDeliveryMethod = (o) => {
    if (o.delivery?.option) return o.delivery.option;
    for (const i of (o.items || [])) {
      const n = (i.product_name || i.package_name || '').toLowerCase();
      if (n.includes('local delivery')) return 'local';
      if (n.includes('lab delivery') || n.includes('direct from lab')) return 'mail';
      if (n.includes('personal delivery')) return 'rural';
    }
    return '';
  };

  const filteredOrders = orders.filter(order => {
    if (deliveryFilters.length < DELIVERY_FILTER_OPTIONS.length) {
      const method = getDeliveryMethod(order) || 'none';
      if (!deliveryFilters.includes(method)) return false;
    }
    const searchLower = search.toLowerCase();
    return (
      order.customer_name?.toLowerCase().includes(searchLower) ||
      order.customer_first_name?.toLowerCase().includes(searchLower) ||
      order.customer_last_name?.toLowerCase().includes(searchLower) ||
      order.customer_email?.toLowerCase().includes(searchLower) ||
      order.id?.toLowerCase().includes(searchLower) ||
      // Pre-order specific fields
      order.child_name?.toLowerCase().includes(searchLower) ||
      order.child_first_name?.toLowerCase().includes(searchLower) ||
      order.child_last_name?.toLowerCase().includes(searchLower) ||
      order.parent_name?.toLowerCase().includes(searchLower) ||
      order.parent_email?.toLowerCase().includes(searchLower) ||
      order.team_name?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Orders</h1>
          <p className="text-slate-600 mt-1">Manage and fulfill customer orders</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={exportToExcel}
            disabled={exportingExcel || orders.length === 0}
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            {exportingExcel ? 'Exporting...' : selectedForPrint.length > 0 ? `Export ${selectedForPrint.length} Selected` : 'Export to Excel'}
          </Button>
          <Button
            variant="outline"
            onClick={exportDeliveryCsv}
            disabled={exportingExcel || orders.length === 0}
            data-testid="export-delivery-csv-btn"
          >
            <FileText className="h-4 w-4 mr-2" />
            Delivery CSV
          </Button>
          {selectedForPrint.length > 0 && (
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={downloadBatchPrintSheets}
              data-testid="batch-print-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              Print {selectedForPrint.length} Orders
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, or order ID..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="search-orders-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectGroup>
                    <SelectLabel>Gallery Orders</SelectLabel>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Pre-Orders</SelectLabel>
                    <SelectItem value="awaiting_photos">Awaiting Photos</SelectItem>
                    <SelectItem value="photos_assigned">Photos Assigned</SelectItem>
                    <SelectItem value="ready_for_review">Ready for Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="fulfilled">Fulfilled</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Both</SelectLabel>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                <SelectTrigger className="w-full sm:w-40" data-testid="payment-filter">
                  <SelectValue placeholder="Payment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payment</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                </SelectContent>
              </Select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-56 justify-between font-normal" data-testid="delivery-filter">
                    <span className="truncate">
                      {deliveryFilters.length === DELIVERY_FILTER_OPTIONS.length
                        ? 'All Delivery Methods'
                        : deliveryFilters.length === 0
                          ? 'No Delivery Methods Selected'
                          : `Delivery: ${deliveryFilters.length} selected`}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64">
                  <DropdownMenuLabel>Delivery methods to show</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {DELIVERY_FILTER_OPTIONS.map(opt => (
                    <DropdownMenuCheckboxItem
                      key={opt.id}
                      checked={deliveryFilters.includes(opt.id)}
                      onCheckedChange={(checked) => {
                        setDeliveryFilters(prev => checked ? [...prev, opt.id] : prev.filter(x => x !== opt.id));
                      }}
                      onSelect={(e) => e.preventDefault()}
                      data-testid={`delivery-filter-${opt.id}`}
                    >
                      {opt.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuCheckboxItem
                    checked={deliveryFilters.length === DELIVERY_FILTER_OPTIONS.length}
                    onCheckedChange={(checked) => {
                      setDeliveryFilters(checked ? DELIVERY_FILTER_OPTIONS.map(o => o.id) : []);
                    }}
                    onSelect={(e) => e.preventDefault()}
                    data-testid="delivery-filter-all"
                  >
                    Select all
                  </DropdownMenuCheckboxItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Date Range Filters */}
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 sm:flex-none">
                <Label className="text-xs text-slate-500 mb-1 block">From Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full sm:w-40"
                  data-testid="start-date-filter"
                />
              </div>
              <div className="flex-1 sm:flex-none">
                <Label className="text-xs text-slate-500 mb-1 block">To Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full sm:w-40"
                  data-testid="end-date-filter"
                />
              </div>
              {(startDate || endDate) && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { setStartDate(''); setEndDate(''); }}
                  className="text-slate-500"
                >
                  Clear Dates
                </Button>
              )}
              <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-amber-50 border-amber-200">
                <Checkbox
                  id="include-preorders"
                  checked={includePreorders}
                  onCheckedChange={setIncludePreorders}
                  data-testid="include-preorders-checkbox"
                />
                <Label htmlFor="include-preorders" className="text-sm text-amber-800 cursor-pointer">
                  Include Pre-Orders
                </Label>
              </div>
              <div className="flex-1 text-right text-sm text-slate-500">
                {filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''} found
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filteredOrders.length > 0 ? (
        <div className="space-y-4">
          {/* Select all shown */}
          <div className="flex items-center gap-3 px-1">
            <Checkbox
              id="select-all-orders"
              checked={filteredOrders.length > 0 && filteredOrders.every(o => selectedForPrint.includes(o.id))}
              onCheckedChange={(checked) => {
                setSelectedForPrint(checked ? filteredOrders.map(o => o.id) : []);
              }}
              data-testid="select-all-orders-checkbox"
            />
            <Label htmlFor="select-all-orders" className="text-sm text-slate-600 cursor-pointer">
              Select all {filteredOrders.length} shown
            </Label>
            {selectedForPrint.length > 0 && (
              <span className="text-sm text-blue-600 font-medium" data-testid="selected-count">
                {selectedForPrint.length} selected
              </span>
            )}
            {selectedForPrint.length > 0 && (
              <Button variant="ghost" size="sm" className="text-slate-500 h-7" onClick={() => setSelectedForPrint([])} data-testid="clear-selection-btn">
                Clear
              </Button>
            )}
          </div>
          {filteredOrders.map((order) => (
            <Card key={order.id} className={`border-slate-200 card-hover ${selectedForPrint.includes(order.id) ? 'ring-2 ring-blue-500' : ''}`}>
              <CardContent className="py-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedForPrint.includes(order.id)}
                        onChange={() => togglePrintSelect(order.id)}
                        className="h-4 w-4 rounded border-slate-300"
                        data-testid={`select-order-${order.id}`}
                      />
                      <div className="bg-slate-100 p-3 rounded-lg">
                        <ShoppingCart className="h-6 w-6 text-slate-600" />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-slate-900">{order.customer_name}</h3>
                        {order.order_type === 'pre-order' && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">Pre-Order</Badge>
                        )}
                        {order.items?.some(i => i.is_addon) && (
                          <Badge
                            className="bg-purple-100 text-purple-700 text-xs"
                            title={order.addon_paid_at ? `Add-on paid ${new Date(order.addon_paid_at).toLocaleString()}` : 'Client added items after the original pre-order'}
                            data-testid={`orders-addon-badge-${order.id}`}
                          >
                            Add-On
                          </Badge>
                        )}
                        <Badge className={getPaymentColor(order.payment_status)}>
                          {getPaymentLabel(order)}
                        </Badge>
                        {order.payment_method && order.payment_method !== 'stripe' && (
                          <Badge variant="outline" className="text-xs">
                            {order.payment_method}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{order.customer_email}</p>
                      {order.order_type === 'pre-order' && order.child_name && (
                        <p className="text-xs text-amber-600">Child: {order.child_name} • {order.event_name}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                        <Clock className="h-3 w-3" />
                        {formatDate(order.created_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-heading font-bold text-slate-900 price-display">
                        {formatCurrency(order.total)}
                      </p>
                      <p className="text-xs text-slate-500">{order.items.length} items</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(order.original_status || order.status)}
                      <Badge className={getStatusColor(order.original_status || order.status)}>
                        {order.original_status || order.status}
                      </Badge>
                    </div>
                    <div className="flex gap-1">
                      {order.payment_status !== 'paid' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setPaymentDialog({ open: true, order })}
                          data-testid={`mark-paid-${order.id}`}
                          title={order.payment_status === 'partial' ? 'Record Additional Payment' : 'Mark Paid'}
                          title="Mark as Paid"
                        >
                          <Banknote className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => downloadPrintSheet(order.id)}
                        data-testid={`print-order-${order.id}`}
                        title="Download Print Sheet"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedOrder(order)}
                        data-testid={`view-order-${order.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <ShoppingCart className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">No orders found</h3>
            <p className="text-slate-500">
              {search || statusFilter !== 'all' || paymentFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Orders will appear here when clients make purchases'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
            <DialogDescription>
              Order ID: {selectedOrder?.id.slice(0, 8)}...
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Customer</p>
                  <p className="font-medium">{selectedOrder.customer_name}</p>
                  <p className="text-sm text-slate-600">{selectedOrder.customer_email}</p>
                  {selectedOrder.customer_phone && (
                    <p className="text-sm text-slate-600">{selectedOrder.customer_phone}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Order Date</p>
                  <p className="font-medium">{formatDate(selectedOrder.created_at)}</p>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {getStatusIcon(selectedOrder.original_status || selectedOrder.status)}
                  <div>
                    <p className="font-medium capitalize">{selectedOrder.original_status || selectedOrder.status}</p>
                    <p className="text-sm text-slate-500">
                      Payment: <span className={selectedOrder.payment_status === 'paid' ? 'text-green-600' : 'text-amber-600'}>
                        {getPaymentLabel(selectedOrder)}
                      </span>
                    </p>
                    {selectedOrder.balance_remaining > 0 && (
                      <p className="text-sm text-amber-600 font-medium">
                        Balance Owing: {formatCurrency(selectedOrder.balance_remaining)}
                      </p>
                    )}
                  </div>
                </div>
                {selectedOrder.payment_status === 'paid' && (
                  <div className="flex items-center gap-2">
                    {selectedOrder.order_type === 'pre-order' &&
                      ['approved', 'processing', 'fulfilled', 'completed'].includes(selectedOrder.original_status || selectedOrder.status) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-amber-500 text-amber-700 hover:bg-amber-50"
                        onClick={() => reopenPreorderFromOrders(selectedOrder)}
                        data-testid="orders-reopen-btn"
                      >
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                        Re-open for Client
                      </Button>
                    )}
                  <Select 
                    value={selectedOrder.original_status || selectedOrder.status} 
                    onValueChange={(v) => updateOrderStatus(selectedOrder.id, v)}
                  >
                    <SelectTrigger className="w-40" data-testid="update-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedOrder.order_type === 'pre-order' ? (
                        <>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="fulfilled">Fulfilled</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  </div>
                )}
              </div>

              {/* Delivery Information */}
              {selectedOrder.delivery && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                    📦 Delivery Information
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-amber-700">Delivery Option</p>
                      <p className="font-medium text-amber-900">{selectedOrder.delivery.option_name || selectedOrder.delivery.option}</p>
                      <p className="text-sm text-amber-700">Fee: {formatCurrency(selectedOrder.delivery.fee)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-amber-700">Address Type</p>
                      <p className="font-medium text-amber-900 capitalize">{selectedOrder.delivery.address_type}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-amber-700">Delivery Address</p>
                      <p className="font-medium text-amber-900">
                        {selectedOrder.delivery.address}<br />
                        {selectedOrder.delivery.city}, {selectedOrder.delivery.postal_code}
                      </p>
                    </div>
                    {selectedOrder.delivery.notes && (
                      <div className="col-span-2">
                        <p className="text-xs text-amber-700">Delivery Notes</p>
                        <p className="text-sm text-amber-900">{selectedOrder.delivery.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div>
                <p className="font-medium text-slate-900 mb-3">Order Items</p>
                <div className="space-y-3">
                  {selectedOrder.items.map((item, index) => {
                    // Get photo info - handle both formats
                    const photoInfo = item.photo || (item.selected_photo_id && selectedOrder.assigned_photos?.find(p => p.id === item.selected_photo_id));
                    const bgInfo = item.background || item.selected_background;
                    const customizations = item.customizations || {};
                    // Filter out internal fields
                    const displayCustomizations = Object.entries(customizations).filter(([k, v]) => v && !['multi_photos', 'back_photo_id'].includes(k));
                    
                    return (
                    <div key={item.product_id || item.package_id || `item-${index}`} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-start gap-4">
                        {/* Show selected photo thumbnail */}
                        {photoInfo?.thumbnail_url && (
                          <img
                            src={photoInfo.thumbnail_url}
                            alt="Photo"
                            className="w-16 h-16 object-cover rounded"
                          />
                        )}
                        <div className="flex-1">
                          <p className="font-medium">
                            {item.package?.name || item.package_name || item.product?.name || item.product_name || 'Item'}
                            {item.is_addon && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs ml-2">Added later</Badge>
                            )}
                          </p>
                          
                          {/* Show selected photo filename or "Not selected" */}
                          {photoInfo ? (
                            <p className="text-sm text-blue-600">
                              📷 Photo: {photoInfo.filename || photoInfo.name || 'Selected'}
                            </p>
                          ) : !item.package_items?.length && (
                            <p className="text-sm text-red-500">
                              📷 Photo: <span className="italic">Not selected</span>
                            </p>
                          )}
                          
                          {/* Show multi-photos for trader cards/blankets */}
                          {item.multi_photos && Object.keys(item.multi_photos).length > 0 && (
                            <div className="text-sm text-blue-600">
                              {Object.entries(item.multi_photos).map(([pos, photoId]) => {
                                const photo = selectedOrder.assigned_photos?.find(p => p.id === photoId);
                                const posLabel = pos === 'front' ? 'Front' : pos === 'back' ? 'Back' : `Photo ${pos.replace('photo', '')}`;
                                return (
                                  <p key={pos}>📷 {posLabel}: {photo?.filename || photoId?.slice(0, 8) || 'N/A'}</p>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Show selected background or "Not selected" for products that need it */}
                          {bgInfo ? (
                            <p className="text-sm text-purple-600">
                              🎨 Background: {typeof bgInfo === 'object' ? bgInfo.name : bgInfo}
                            </p>
                          ) : item.selected_background_id ? (
                            <p className="text-sm text-purple-600">
                              🎨 Background: {backgrounds[item.selected_background_id] || item.selected_background_id.slice(0, 8) + '...'}
                            </p>
                          ) : !item.package_items?.length && (
                            <p className="text-sm text-red-500">
                              🎨 Background: <span className="italic">Not selected</span>
                            </p>
                          )}
                          
                          {/* Show customizations or "Not entered" */}
                          {displayCustomizations.length > 0 ? (
                            <div className="text-sm text-amber-700 mt-1">
                              ✏️ {displayCustomizations.map(([key, value]) => (
                                <span key={key} className="mr-2 capitalize">{key}: {value}</span>
                              ))}
                            </div>
                          ) : !item.package_items?.length && (
                            <p className="text-sm text-red-500">
                              ✏️ Customizations: <span className="italic">Not entered</span>
                            </p>
                          )}
                          
                          {/* Show selected variations for products */}
                          {item.selected_variations && Object.keys(item.selected_variations).length > 0 && (
                            <div className="text-sm text-slate-700 mt-1" data-testid={`item-variations-${index}`}>
                              🏷️ {Object.entries(item.selected_variations).map(([varName, option]) => {
                                const optName = (option && typeof option === 'object') ? option.name : option;
                                const adj = (option && typeof option === 'object') ? option.price_adjustment : 0;
                                return (
                                  <span key={varName} className="mr-2">
                                    {varName}: <span className="font-medium">{optName || 'Not chosen'}</span>
                                    {adj > 0 && ` (+$${adj.toFixed(2)})`}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-medium">×{item.quantity}</p>
                          <p className="text-sm text-slate-500">
                            {formatCurrency((item.package?.price || item.product?.price || item.price || 0) * item.quantity)}
                          </p>
                          {/* Download composite button for items with customizations */}
                          {displayCustomizations.length > 0 && (bgInfo || item.selected_background_id) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-2 h-7 text-xs"
                              onClick={() => {
                                const url = `${API}/orders/${selectedOrder.id}/composite/${index}`;
                                window.open(url, '_blank');
                              }}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Download
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Package Items - show individual product configurations */}
                      {item.package_items && item.package_items.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                          <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Package Contents:</p>
                          {item.package_items.map((pkgItem, pkgIndex) => {
                            const pkgPhotoInfo = pkgItem.photo || (pkgItem.selected_photo_id && selectedOrder.assigned_photos?.find(p => p.id === pkgItem.selected_photo_id));
                            const pkgBgInfo = pkgItem.background || pkgItem.selected_background;
                            const pkgCustomizations = pkgItem.customizations || {};
                            const pkgDisplayCustomizations = Object.entries(pkgCustomizations).filter(([k, v]) => v && !['multi_photos', 'back_photo_id'].includes(k));
                            
                            return (
                            <div key={`${pkgItem.product_id || 'pkg'}-${pkgIndex}`} className="flex items-start gap-3 p-2 bg-white rounded border border-slate-200">
                              {pkgPhotoInfo?.thumbnail_url && (
                                <img
                                  src={pkgPhotoInfo.thumbnail_url}
                                  alt="Photo"
                                  className="w-12 h-12 object-cover rounded"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800">
                                  {pkgItem.product?.name || pkgItem.product_name || 'Product'} 
                                  <span className="text-slate-500 font-normal"> ×{pkgItem.quantity}</span>
                                </p>
                                
                                {/* Package item photo or "Not selected" */}
                                {pkgPhotoInfo ? (
                                  <p className="text-xs text-blue-600">
                                    📷 {pkgPhotoInfo.filename || 'Selected'}
                                  </p>
                                ) : (
                                  <p className="text-xs text-red-500">
                                    📷 <span className="italic">Not selected</span>
                                  </p>
                                )}
                                
                                {/* Package item multi-photos */}
                                {pkgItem.multi_photos && Object.keys(pkgItem.multi_photos).length > 0 && (
                                  <div className="text-xs text-blue-600">
                                    {Object.entries(pkgItem.multi_photos).map(([pos, photoId]) => {
                                      const photo = selectedOrder.assigned_photos?.find(p => p.id === photoId);
                                      const posLabel = pos === 'front' ? 'Front' : pos === 'back' ? 'Back' : `Photo ${pos.replace('photo', '')}`;
                                      return (
                                        <span key={pos} className="mr-2">📷 {posLabel}: {photo?.filename || photoId?.slice(0, 8)}</span>
                                      );
                                    })}
                                  </div>
                                )}
                                
                                {/* Package item background or "Not selected" */}
                                {pkgBgInfo ? (
                                  <p className="text-xs text-purple-600">
                                    🎨 {typeof pkgBgInfo === 'object' ? pkgBgInfo.name : pkgBgInfo}
                                  </p>
                                ) : pkgItem.selected_background_id ? (
                                  <p className="text-xs text-purple-600">
                                    🎨 Background: {backgrounds[pkgItem.selected_background_id] || pkgItem.selected_background_id.slice(0, 8) + '...'}
                                  </p>
                                ) : (
                                  <p className="text-xs text-red-500">
                                    🎨 <span className="italic">Not selected</span>
                                  </p>
                                )}
                                
                                {/* Package item variations (e.g., keychain style, magnetic backing) */}
                                {pkgItem.selected_variations && Object.keys(pkgItem.selected_variations).length > 0 && (
                                  <p className="text-xs text-slate-700">
                                    🏷️ {Object.entries(pkgItem.selected_variations).map(([k, v]) => `${k}: ${(v && typeof v === 'object') ? v.name : v}`).join(', ')}
                                  </p>
                                )}
                                
                                {/* Package item customizations or "Not entered" */}
                                {pkgDisplayCustomizations.length > 0 ? (
                                  <p className="text-xs text-amber-700">
                                    ✏️ {pkgDisplayCustomizations.map(([k, v]) => `${k}: ${v}`).join(', ')}
                                  </p>
                                ) : (
                                  <p className="text-xs text-red-500">
                                    ✏️ <span className="italic">Not entered</span>
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                          })}
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
              </div>

              {/* Bonus Items (Pre-orders only) */}
              {selectedOrder.order_type === 'pre-order' && (selectedOrder.includes_free_digital || selectedOrder.includes_free_wallets) && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="font-medium text-green-800 mb-2">🎁 Free Bonus Items</p>
                  
                  {/* Free Digital */}
                  {selectedOrder.includes_free_digital && (
                    <div className="text-sm text-green-700 mb-2">
                      <p className="font-medium">Free Digital Image (watermarked)</p>
                      {selectedOrder.free_digital_photo_id ? (
                        <>
                          <p className="text-xs">
                            📷 Photo: {selectedOrder.assigned_photos?.find(p => p.id === selectedOrder.free_digital_photo_id)?.filename || selectedOrder.free_digital_photo_id.slice(0, 8)}
                          </p>
                          {selectedOrder.free_digital_background_id && (
                            <p className="text-xs text-purple-600">
                              🎨 Background: {backgrounds[selectedOrder.free_digital_background_id] || selectedOrder.free_digital_background_id.slice(0, 8)}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-amber-600">Photo not yet selected</p>
                      )}
                    </div>
                  )}
                  
                  {/* Free Wallets */}
                  {selectedOrder.includes_free_wallets && (
                    <div className="text-sm text-green-700">
                      <p className="font-medium">2 Free Wallet-Sized Photos</p>
                      {selectedOrder.bonus_wallet_photos?.photo1 && (
                        <p className="text-xs">
                          📷 Wallet 1: {selectedOrder.assigned_photos?.find(p => p.id === selectedOrder.bonus_wallet_photos.photo1)?.filename || 'Selected'}
                          {selectedOrder.bonus_wallet_backgrounds?.bg1 && (
                            <span className="text-purple-600"> + 🎨 {backgrounds[selectedOrder.bonus_wallet_backgrounds.bg1] || 'Background'}</span>
                          )}
                        </p>
                      )}
                      {selectedOrder.bonus_wallet_photos?.photo2 && (
                        <p className="text-xs">
                          📷 Wallet 2: {selectedOrder.assigned_photos?.find(p => p.id === selectedOrder.bonus_wallet_photos.photo2)?.filename || 'Selected'}
                          {selectedOrder.bonus_wallet_backgrounds?.bg2 && (
                            <span className="text-purple-600"> + 🎨 {backgrounds[selectedOrder.bonus_wallet_backgrounds.bg2] || 'Background'}</span>
                          )}
                        </p>
                      )}
                      {!selectedOrder.bonus_wallet_photos?.photo1 && !selectedOrder.bonus_wallet_photos?.photo2 && (
                        <p className="text-xs text-amber-600">Photos not yet selected</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Totals */}
              <div className="border-t pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span>{formatCurrency(selectedOrder.subtotal || 0)}</span>
                </div>
                {(selectedOrder.discount_amount > 0 || selectedOrder.discount_code) && (
                  <div className="flex justify-between text-sm mt-1 text-green-600">
                    <span>Discount {selectedOrder.discount_code && `(${selectedOrder.discount_code})`}</span>
                    <span>-{formatCurrency(selectedOrder.discount_amount || 0)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-slate-500">Tax</span>
                  <span>{formatCurrency(selectedOrder.tax || 0)}</span>
                </div>
                {selectedOrder.delivery && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-500">Delivery ({selectedOrder.delivery.option_name || selectedOrder.delivery.option || 'Standard'})</span>
                    <span>{formatCurrency(selectedOrder.delivery.fee || 0)}</span>
                  </div>
                )}
                {selectedOrder.cash_discount > 0 && (
                  <div className="flex justify-between text-sm mt-1 text-green-600">
                    <span>Cash Discount (10%)</span>
                    <span>-{formatCurrency(selectedOrder.cash_discount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg mt-3">
                  <span>Total</span>
                  <span className="price-display">{formatCurrency(selectedOrder.total)}</span>
                </div>
                {selectedOrder.payment_method && (
                  <p className="text-sm text-slate-500 mt-2">
                    Paid via: {selectedOrder.payment_method.toUpperCase()}
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            {selectedOrder?.payment_status !== 'paid' && (
              <Button 
                variant="outline" 
                onClick={() => {
                  setPaymentDialog({ open: true, order: selectedOrder });
                  setSelectedOrder(null);
                }}
              >
                <Banknote className="h-4 w-4 mr-2" />
                Mark Paid
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => selectedOrder && downloadPrintSheet(selectedOrder.id)}
            >
              <FileText className="h-4 w-4 mr-2" />
              Print Sheet
            </Button>
            <Button variant="outline" onClick={() => setSelectedOrder(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offline Payment Dialog */}
      <Dialog open={paymentDialog.open} onOpenChange={(open) => { 
        setPaymentDialog({ open, order: null }); 
        setPaymentAmount('');
        setPaymentNotes('');
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Offline Payment</DialogTitle>
            <DialogDescription>
              Record a payment via e-Transfer, cash, or cheque. You can enter a partial amount.
            </DialogDescription>
          </DialogHeader>
          {paymentDialog.order && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{paymentDialog.order.customer_name}</p>
                <p className="text-sm text-slate-500">{paymentDialog.order.customer_email}</p>
                <div className="mt-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Order Total:</span>
                    <span>{formatCurrency(paymentDialog.order.total)}</span>
                  </div>
                  {(paymentDialog.order.amount_paid > 0) && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Amount Paid:</span>
                      <span>{formatCurrency(paymentDialog.order.amount_paid)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>Balance Owing:</span>
                    <span className="text-amber-600">
                      {formatCurrency((paymentDialog.order.total || 0) - (paymentDialog.order.amount_paid || 0))}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Payment Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={(paymentDialog.order.total || 0) - (paymentDialog.order.amount_paid || 0)}
                    placeholder={((paymentDialog.order.total || 0) - (paymentDialog.order.amount_paid || 0)).toFixed(2)}
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="pl-7"
                    data-testid="payment-amount-input"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Leave empty to pay full balance. Enter a partial amount for split payments.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="etransfer">e-Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="e.g., Reference number, date received..."
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  data-testid="payment-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPaymentDialog({ open: false, order: null }); setPaymentAmount(''); setPaymentNotes(''); }}>
              Cancel
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={markOfflinePayment}
              data-testid="confirm-payment-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
